/**
 * Shared channel/status presentation metadata for the omnichannel inbox.
 */
import type { LucideIcon } from 'lucide-react';
import { Facebook, Instagram, Mail, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface ChannelMeta {
  label: string;
  icon: LucideIcon;
  /** Icon/text tint. */
  fg: string;
  /** Soft background for avatars/chips. */
  bg: string;
}

export const CHANNEL_META: Record<string, ChannelMeta> = {
  whatsapp_customer: {
    label: 'WhatsApp',
    icon: MessageCircle,
    fg: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500/10',
  },
  instagram: {
    label: 'Instagram',
    icon: Instagram,
    fg: 'text-fuchsia-600 dark:text-fuchsia-400',
    bg: 'bg-fuchsia-500/10',
  },
  facebook_messenger: {
    label: 'Messenger',
    icon: Facebook,
    fg: 'text-sky-600 dark:text-sky-400',
    bg: 'bg-sky-500/10',
  },
  gmail: {
    label: 'Email',
    icon: Mail,
    fg: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-500/10',
  },
};

export function channelMeta(channel: string): ChannelMeta {
  return (
    CHANNEL_META[channel] ?? {
      label: channel,
      icon: MessageCircle,
      fg: 'text-muted-foreground',
      bg: 'bg-muted',
    }
  );
}

export const STATUS_META: Record<string, { label: string; tone: 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info' }> = {
  bot_handling: { label: 'AI handling', tone: 'info' },
  awaiting_agent: { label: 'Needs agent', tone: 'warning' },
  agent_active: { label: 'Agent active', tone: 'primary' },
  closed: { label: 'Closed', tone: 'neutral' },
};

export function ChannelIcon({ channel, className }: { channel: string; className?: string }) {
  const meta = channelMeta(channel);
  const Icon = meta.icon;
  return <Icon className={cn('h-3.5 w-3.5', meta.fg, className)} />;
}

export function ContactAvatar({
  name,
  channel,
  size = 'md',
}: {
  name: string;
  channel: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const meta = channelMeta(channel);
  const Icon = meta.icon;
  const initials = (name || '?')
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const dims = size === 'lg' ? 'h-11 w-11 text-sm' : size === 'sm' ? 'h-7 w-7 text-[10px]' : 'h-9 w-9 text-xs';
  const badgeDims = size === 'sm' ? 'h-3.5 w-3.5 -right-0.5 -bottom-0.5' : 'h-4 w-4 -right-1 -bottom-1';
  return (
    <div className="relative shrink-0">
      <div
        className={cn(
          'flex items-center justify-center rounded-full bg-primary/10 font-semibold text-primary',
          dims,
        )}
      >
        {initials || '?'}
      </div>
      <span
        className={cn(
          'absolute flex items-center justify-center rounded-full bg-surface ring-1 ring-border',
          badgeDims,
          'p-0.5',
        )}
      >
        <Icon className={cn('h-full w-full', meta.fg)} />
      </span>
    </div>
  );
}

export function contactLabel(contact?: {
  display_name?: string;
  email?: string;
  phone?: string;
  external_id?: string;
} | null): string {
  return contact?.display_name || contact?.email || contact?.phone || contact?.external_id || 'Unknown';
}
